import React, { useMemo, useState, useEffect } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormTabs, tabHasFieldErrors } from "../FormTabs";
import { FormHelpCallout } from "../FormHelpCallout";
import type {
  CreateSubscriptionTierData,
  RevenueModel,
} from "../../../types/startup.types";

export interface SubscriptionTierFormProps {
  onSubmit: (data: CreateSubscriptionTierData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  initialData?: Partial<CreateSubscriptionTierData>;
  isLoading?: boolean;
  isEditing?: boolean;
}

const BILLING_FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
] as const;

const REVENUE_MODELS: Array<{ value: RevenueModel; label: string }> = [
  { value: "subscription", label: "Subscription (recurring fee)" },
  { value: "per_visit", label: "Per visit / transaction" },
  { value: "hybrid", label: "Hybrid (subscription + visits)" },
];

type TabId = "general" | "pricing" | "seasonality";

type TierFormData = {
  name: string;
  description?: string;
  revenueModel: RevenueModel;
  price: number;
  billingFrequency: "monthly" | "quarterly" | "yearly";
  pricePerVisit?: number;
  avgVisitsPerCustomerPerMonth?: number;
  q1Seasonality?: number;
  q2Seasonality?: number;
  q3Seasonality?: number;
  q4Seasonality?: number;
  features?: string;
};

const TAB_FIELDS: Record<TabId, (keyof TierFormData)[]> = {
  general: ["name", "description", "revenueModel", "features"],
  pricing: ["price", "billingFrequency", "pricePerVisit", "avgVisitsPerCustomerPerMonth"],
  seasonality: ["q1Seasonality", "q2Seasonality", "q3Seasonality", "q4Seasonality"],
};

function firstTabWithErrors(errors: FieldErrors<TierFormData>): TabId {
  const order: TabId[] = ["general", "pricing", "seasonality"];
  return (
    order.find((tabId) => tabHasFieldErrors(errors, TAB_FIELDS[tabId])) ?? "general"
  );
}

export const SubscriptionTierForm: React.FC<SubscriptionTierFormProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  initialData,
  isLoading = false,
  isEditing = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>("general");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TierFormData>({
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      revenueModel: initialData?.revenueModel || "subscription",
      price: initialData?.price || 0,
      billingFrequency: initialData?.billingFrequency || "monthly",
      pricePerVisit: initialData?.pricePerVisit || 0,
      avgVisitsPerCustomerPerMonth:
        initialData?.avgVisitsPerCustomerPerMonth || 1,
      q1Seasonality: initialData?.visitSeasonality?.[1] ?? 1,
      q2Seasonality: initialData?.visitSeasonality?.[4] ?? 1,
      q3Seasonality: initialData?.visitSeasonality?.[7] ?? 1,
      q4Seasonality: initialData?.visitSeasonality?.[10] ?? 1,
      features: initialData?.features ? initialData.features.join("\n") : "",
    },
  });

  const revenueModel = watch("revenueModel");
  const showSubscription =
    revenueModel === "subscription" || revenueModel === "hybrid";
  const showVisits = revenueModel === "per_visit" || revenueModel === "hybrid";

  useEffect(() => {
    if (!showVisits && activeTab === "seasonality") {
      setActiveTab("pricing");
    }
  }, [showVisits, activeTab]);

  const tabs = useMemo(() => {
    const items: Array<{ id: TabId; label: string; hasError?: boolean }> = [
      {
        id: "general",
        label: "General",
        hasError: tabHasFieldErrors(errors, TAB_FIELDS.general),
      },
      {
        id: "pricing",
        label: "Pricing",
        hasError: tabHasFieldErrors(errors, TAB_FIELDS.pricing),
      },
    ];

    if (showVisits) {
      items.push({
        id: "seasonality",
        label: "Seasonality",
        hasError: tabHasFieldErrors(errors, TAB_FIELDS.seasonality),
      });
    }

    return items;
  }, [errors, showVisits]);

  const handleFormSubmit = (data: TierFormData) => {
    const visitSeasonality =
      showVisits &&
      (data.q1Seasonality !== 1 ||
        data.q2Seasonality !== 1 ||
        data.q3Seasonality !== 1 ||
        data.q4Seasonality !== 1)
        ? {
            1: Number(data.q1Seasonality ?? 1),
            2: Number(data.q1Seasonality ?? 1),
            3: Number(data.q1Seasonality ?? 1),
            4: Number(data.q2Seasonality ?? 1),
            5: Number(data.q2Seasonality ?? 1),
            6: Number(data.q2Seasonality ?? 1),
            7: Number(data.q3Seasonality ?? 1),
            8: Number(data.q3Seasonality ?? 1),
            9: Number(data.q3Seasonality ?? 1),
            10: Number(data.q4Seasonality ?? 1),
            11: Number(data.q4Seasonality ?? 1),
            12: Number(data.q4Seasonality ?? 1),
          }
        : undefined;

    onSubmit({
      name: data.name,
      description: data.description,
      revenueModel: data.revenueModel,
      price: Number(data.price) || 0,
      billingFrequency: data.billingFrequency,
      pricePerVisit: showVisits ? Number(data.pricePerVisit) || 0 : undefined,
      avgVisitsPerCustomerPerMonth: showVisits
        ? Number(data.avgVisitsPerCustomerPerMonth) || 0
        : undefined,
      visitSeasonality,
      startupId: "",
      features: data.features
        ? data.features.split("\n").filter((line) => line.trim() !== "")
        : [],
    });
  };

  const handleValidationError = (formErrors: FieldErrors<TierFormData>) => {
    setActiveTab(firstTabWithErrors(formErrors));
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit, handleValidationError)}
      className="space-y-4"
    >
      <FormTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as TabId)}
      />

      <div className="min-h-[240px] pt-1">
        {activeTab === "general" ? (
          <div className="space-y-4">
            <FormHelpCallout
              learnMoreHref="/help/revenue-plans"
              learnMoreLabel="How revenue plans work"
            >
              Revenue plans define <strong className="text-white/75">pricing</strong>{" "}
              only. Customer growth and location limits are configured in customer
              projections.
            </FormHelpCallout>

            <FormField label="Revenue plan name" error={errors.name?.message}>
              <Input
                {...register("name", { required: "Name is required" })}
                placeholder="e.g., Standard visit, Pro subscription"
              />
            </FormField>

            <FormField label="Description (optional)" error={errors.description?.message}>
              <Input
                {...register("description")}
                placeholder="Brief description of this pricing plan"
              />
            </FormField>

            <FormField
              label="Revenue model"
              error={errors.revenueModel?.message}
              helperText="Subscription = recurring fee. Per visit = transaction-based. Hybrid = both."
            >
              <select {...register("revenueModel")} className="sh-select">
                {REVENUE_MODELS.map((model) => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Features (optional)">
              <textarea
                {...register("features")}
                placeholder="Enter features separated by new lines..."
                className="sh-select min-h-[80px]"
              />
            </FormField>
          </div>
        ) : null}

        {activeTab === "pricing" ? (
          <div className="space-y-4">
            {showSubscription ? (
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Subscription price ($)" error={errors.price?.message}>
                  <Input
                    type="number"
                    step="0.01"
                    {...register("price", {
                      required: showSubscription ? "Price is required" : false,
                      min: { value: 0, message: "Must be 0 or greater" },
                      valueAsNumber: true,
                    })}
                  />
                </FormField>
                <FormField
                  label="Billing frequency"
                  error={errors.billingFrequency?.message}
                >
                  <select {...register("billingFrequency")} className="sh-select">
                    {BILLING_FREQUENCIES.map((freq) => (
                      <option key={freq.value} value={freq.value}>
                        {freq.label}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
            ) : null}

            {showVisits ? (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Price per visit ($)"
                  error={errors.pricePerVisit?.message}
                >
                  <Input
                    type="number"
                    step="0.01"
                    {...register("pricePerVisit", {
                      required: showVisits ? "Visit price is required" : false,
                      min: { value: 0, message: "Must be 0 or greater" },
                      valueAsNumber: true,
                    })}
                  />
                </FormField>
                <FormField
                  label="Avg visits / customer / month"
                  error={errors.avgVisitsPerCustomerPerMonth?.message}
                >
                  <Input
                    type="number"
                    step="0.01"
                    {...register("avgVisitsPerCustomerPerMonth", {
                      required: showVisits ? "Visit frequency is required" : false,
                      min: { value: 0, message: "Must be 0 or greater" },
                      valueAsNumber: true,
                    })}
                  />
                </FormField>
              </div>
            ) : null}

            {!showSubscription && !showVisits ? (
              <p className="text-sm text-white/45">
                Select a revenue model on the General tab to configure pricing.
              </p>
            ) : null}
          </div>
        ) : null}

        {activeTab === "seasonality" && showVisits ? (
          <div className="space-y-4">
            <p className="text-xs text-white/45">
              Seasonality multiplies expected visits (1.0 = normal, 1.2 = 20% busier).
            </p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <FormField label="Q1 seasonality">
                <Input
                  type="number"
                  step="0.1"
                  {...register("q1Seasonality", { valueAsNumber: true })}
                />
              </FormField>
              <FormField label="Q2 seasonality">
                <Input
                  type="number"
                  step="0.1"
                  {...register("q2Seasonality", { valueAsNumber: true })}
                />
              </FormField>
              <FormField label="Q3 seasonality">
                <Input
                  type="number"
                  step="0.1"
                  {...register("q3Seasonality", { valueAsNumber: true })}
                />
              </FormField>
              <FormField label="Q4 seasonality">
                <Input
                  type="number"
                  step="0.1"
                  {...register("q4Seasonality", { valueAsNumber: true })}
                />
              </FormField>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex gap-3 border-t border-white/10 pt-4">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? "Saving..." : "Save revenue plan"}
        </Button>
        {isEditing && onDelete ? (
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={onDelete}
            disabled={isLoading}
          >
            Delete
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
